/**
 * AnnotationLayer — manages a 2D canvas overlay on top of a static image.
 *
 * Analogous to OverlayLayer but operates in pure image-pixel space with
 * NO dependency on IViewport or VTK.
 */

import { TextAnnotation } from './objects/text-annotation';
import type { IAnnotationObject } from './types';

export class AnnotationLayer {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private objects = new Map<string, IAnnotationObject>();
    /** Coordinate space = the source image's natural resolution. */
    private imageWidth = 0;
    private imageHeight = 0;

    constructor(container: HTMLElement) {
        this.canvas = this.createCanvas(container);
        this.ctx = this.canvas.getContext('2d')!;
    }

    private createCanvas(container: HTMLElement): HTMLCanvasElement {
        const canvas = document.createElement('canvas');
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.pointerEvents = 'auto';
        canvas.style.zIndex = '10';

        // The canvas never takes focus. Without this, mousedown's default
        // action blurs the in-place text editor the instant it opens (the
        // browser processes mousedown after React has already mounted and
        // focused the editor from the preceding pointerdown).
        canvas.addEventListener('mousedown', (e) => e.preventDefault());

        if (!container.classList.contains('relative')) {
            container.classList.add('relative');
        }
        container.appendChild(canvas);

        return canvas;
    }

    getCanvas(): HTMLCanvasElement {
        return this.canvas;
    }

    // ── Object management ──

    addObject(obj: IAnnotationObject): void {
        if (this.objects.has(obj.id)) {
            throw new Error(`Annotation "${obj.id}" is already registered`);
        }
        this.objects.set(obj.id, obj);
    }

    removeObject(id: string): void {
        this.objects.delete(id);
    }

    getObject(id: string): IAnnotationObject | null {
        return this.objects.get(id) ?? null;
    }

    getAllObjects(): IAnnotationObject[] {
        return Array.from(this.objects.values());
    }

    clear(): void {
        this.objects.clear();
    }

    // ── Hit testing ──

    hitTest(x: number, y: number): IAnnotationObject | null {
        const sorted = Array.from(this.objects.values())
            .filter((obj) => obj.visible && obj.interactive)
            .sort((a, b) => b.zIndex - a.zIndex);

        for (const obj of sorted) {
            if (obj.hitTest(x, y)) {
                return obj;
            }
        }
        return null;
    }

    // ── Rendering ──

    /**
     * Set the annotation coordinate space to the image's natural resolution.
     *
     * The canvas backing store matches natural pixels; CSS stretches it to the
     * displayed size. Annotation coordinates are therefore resolution-independent
     * — resizing the editor panel only changes the CSS box, never the stored
     * geometry — and the composite is a 1:1 copy, so on-screen == exported.
     */
    setImageSize(naturalWidth: number, naturalHeight: number): void {
        this.imageWidth = naturalWidth;
        this.imageHeight = naturalHeight;

        this.canvas.width = naturalWidth;
        this.canvas.height = naturalHeight;
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';

        this.render();
    }

    render(): void {
        this.ctx.clearRect(0, 0, this.imageWidth, this.imageHeight);

        const sorted = Array.from(this.objects.values())
            .filter((obj) => obj.visible)
            .sort((a, b) => a.zIndex - b.zIndex);

        for (const obj of sorted) {
            obj.render(this.ctx);
        }
    }

    setAllVisible(visible: boolean): void {
        for (const obj of this.objects.values()) {
            obj.visible = visible;
        }
        this.render();
    }

    /**
     * Clear transient selection/hover decorations on every object so a
     * subsequent render() draws only the annotation shapes. Used before
     * compositing the export image so handles are never baked in.
     */
    clearDecorations(): void {
        for (const obj of this.objects.values()) {
            obj.selected = false;
            obj.clearHoverState();
        }
    }

    /**
     * Update a text annotation's text content. Returns true if the object exists
     * and is a text annotation. Keeps type-specific knowledge out of React.
     */
    setAnnotationText(id: string, text: string): boolean {
        const obj = this.objects.get(id);
        if (!(obj instanceof TextAnnotation)) return false;
        obj.setText(text);
        this.render();
        return true;
    }

    /**
     * Toggle the transient in-place-editing flag on a text annotation, which
     * suppresses its canvas rendering and hit testing while the DOM overlay
     * editor is active. Returns true if the object is a text annotation.
     */
    setTextEditing(id: string, editing: boolean): boolean {
        const obj = this.objects.get(id);
        if (!(obj instanceof TextAnnotation)) return false;
        obj.isEditing = editing;
        this.render();
        return true;
    }

    /** Current text of a text annotation, or null for any other object. */
    getTextAnnotationText(id: string): string | null {
        const obj = this.objects.get(id);
        return obj instanceof TextAnnotation ? obj.text : null;
    }

    // ── Coordinate helpers ──

    /**
     * Convert a pointer event's client (viewport) coordinates to image-natural
     * pixel coordinates, accounting for the CSS scaling of the canvas.
     */
    canvasToImageCoords(clientX: number, clientY: number): { x: number; y: number } {
        const rect = this.canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
        return {
            x: (clientX - rect.left) * (this.imageWidth / rect.width),
            y: (clientY - rect.top) * (this.imageHeight / rect.height),
        };
    }

    /** CSS pixels per image pixel; 0 while the layer is unsized or unlaid-out. */
    getDisplayScale(): number {
        if (this.imageWidth === 0) return 0;
        const rect = this.canvas.getBoundingClientRect();
        return rect.width / this.imageWidth;
    }

    // ── Cleanup ──

    dispose(): void {
        this.objects.clear();
        if (this.canvas.parentElement) {
            this.canvas.parentElement.removeChild(this.canvas);
        }
    }
}
